import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Ticket, CreditCard, Check, X, Loader2, ArrowRight, Tag, Shield, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentGate = ({ userData, onSuccess, onBack, isHindi }) => {
  const [plan, setPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState(null); // null, 'validating', 'valid', 'invalid'
  const [couponResult, setCouponResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);

  // Fetch plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/public/plans`);
        if (response.data && response.data.length > 0) {
          // Find default plan or use first
          const defaultPlan = response.data.find(p => p.is_default) || response.data[0];
          setPlan(defaultPlan);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        // Set fallback plan with correct price (39000)
        console.warn('Using fallback plan - API fetch failed');
        setPlan({ name: '3 Month Wellness Plan', price: 39000, currency: 'INR' });
      }
    };
    fetchPlans();
  }, []);

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponStatus('validating');
    try {
      const response = await axios.post(`${API}/public/coupons/validate`, {
        code: couponCode.trim(),
        plan_id: plan?.id
      });
      
      if (response.data.valid) {
        setCouponStatus('valid');
        setCouponResult(response.data);
      } else {
        setCouponStatus('invalid');
        setCouponResult(response.data);
        toast.error(response.data.message);
      }
    } catch (error) {
      setCouponStatus('invalid');
      toast.error(error.response?.data?.detail || 'Failed to validate coupon');
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponResult || couponResult.final_amount > 0) {
      toast.error('Please enter a valid coupon code');
      return;
    }
    
    setIsProcessing(true);
    try {
      // Create order with coupon (100% discount = immediate success)
      const response = await axios.post(`${API}/public/orders`, {
        plan_id: plan?.id || 'default',
        name: userData.name,
        email: userData.email,
        mobile: userData.mobile,
        coupon_code: couponCode.trim()
      });
      
      if (response.data.payment_status === 'success') {
        toast.success(isHindi ? 'कूपन सफलतापूर्वक लागू हुआ!' : 'Coupon applied successfully!');
        onSuccess(response.data.order_id);
      } else {
        // Partial discount - need payment
        toast.info(isHindi ? 'भुगतान आवश्यक है' : 'Payment required for remaining amount');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process coupon');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayNow = () => {
    toast.info(isHindi ? 'भुगतान गेटवे जल्द आ रहा है!' : 'Payment gateway coming soon!');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  if (!plan) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-5 md:space-y-8 max-w-lg mx-auto"
    >
      {/* Plan Card */}
      <div className="bg-white rounded-2xl md:rounded-3xl border-2 border-[#E5E0D1] p-4 md:p-6 shadow-lg">
        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-[#D2E823]/20 rounded-xl md:rounded-2xl flex items-center justify-center">
            <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-[#1A4D2E]" />
          </div>
          <div>
            <h3 className="font-serif font-bold text-[#1A4D2E] text-base md:text-lg">{plan.name}</h3>
            <p className="text-xs md:text-sm text-gray-500">{plan.description || (isHindi ? 'व्यक्तिगत आहार योजना' : 'Personalized wellness plan')}</p>
          </div>
        </div>
        
        <div className="flex items-baseline gap-2 mb-3 md:mb-4">
          <span className="text-3xl md:text-4xl font-bold text-[#1A4D2E]">{formatPrice(plan.price)}</span>
          {couponResult?.valid && couponResult.final_amount === 0 && (
            <span className="text-base md:text-lg text-gray-400 line-through">{formatPrice(plan.price)}</span>
          )}
        </div>

        {couponResult?.valid && couponResult.final_amount === 0 && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#D2E823]/20 rounded-xl p-2.5 md:p-3 mb-3 md:mb-4"
          >
            <div className="flex items-center gap-2 text-[#1A4D2E]">
              <Check className="w-4 h-4 md:w-5 md:h-5" />
              <span className="font-medium text-sm md:text-base">
                {isHindi ? '100% छूट लागू - निःशुल्क!' : '100% discount applied - FREE!'}
              </span>
            </div>
          </motion.div>
        )}

        <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D2E823] shrink-0" />
            <span>{isHindi ? '3 महीने का आहार योजना' : '3 months personalized diet plan'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D2E823] shrink-0" />
            <span>{isHindi ? 'AI-संचालित अनुशंसाएं' : 'AI-powered recommendations'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D2E823] shrink-0" />
            <span>{isHindi ? 'तुरंत PDF डाउनलोड' : 'Instant PDF download'}</span>
          </div>
        </div>
      </div>

      {/* Payment Options */}
      <div className="space-y-3 md:space-y-4">
        {/* Coupon Section */}
        <div className="bg-white rounded-xl md:rounded-2xl border-2 border-[#E5E0D1] overflow-hidden">
          <button
            onClick={() => setShowCouponInput(!showCouponInput)}
            className="w-full p-3 md:p-4 flex items-center justify-between active:bg-[#F5F1E8]/50 transition-colors min-h-[56px] touch-manipulation"
            data-testid="toggle-coupon-btn"
          >
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-9 h-9 md:w-10 md:h-10 bg-[#D2E823]/20 rounded-lg md:rounded-xl flex items-center justify-center">
                <Ticket className="w-4 h-4 md:w-5 md:h-5 text-[#1A4D2E]" />
              </div>
              <span className="font-medium text-[#1A4D2E] text-sm md:text-base">
                {isHindi ? 'क्या आपके पास कूपन कोड है?' : 'Have a coupon code?'}
              </span>
            </div>
            <motion.div
              animate={{ rotate: showCouponInput ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
            </motion.div>
          </button>

          {showCouponInput && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="px-3 md:px-4 pb-3 md:pb-4"
            >
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type="text"
                    placeholder={isHindi ? 'कूपन कोड दर्ज करें' : 'Enter coupon code'}
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponStatus(null);
                      setCouponResult(null);
                    }}
                    className={cn(
                      "h-11 md:h-12 text-center uppercase font-mono text-base md:text-lg tracking-wider rounded-lg md:rounded-xl border-2 touch-manipulation",
                      couponStatus === 'valid' && "border-green-500 bg-green-50",
                      couponStatus === 'invalid' && "border-red-500 bg-red-50"
                    )}
                    data-testid="coupon-input"
                  />
                  {couponStatus === 'valid' && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-green-500" />
                  )}
                  {couponStatus === 'invalid' && (
                    <X className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-red-500" />
                  )}
                </div>
                <Button
                  onClick={validateCoupon}
                  disabled={!couponCode.trim() || couponStatus === 'validating'}
                  className="h-11 md:h-12 px-4 md:px-6 bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 rounded-lg md:rounded-xl touch-manipulation"
                  data-testid="validate-coupon-btn"
                >
                  {couponStatus === 'validating' ? (
                    <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                  ) : (
                    isHindi ? 'जांचें' : 'Apply'
                  )}
                </Button>
              </div>

              {couponResult?.valid && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 md:mt-3 p-2.5 md:p-3 bg-green-50 rounded-lg md:rounded-xl border border-green-200"
                >
                  <div className="flex items-center justify-between text-xs md:text-sm">
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <Tag className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-600" />
                      <span className="text-green-700 font-medium">
                        {couponResult.discount_type === 'percentage' 
                          ? `${couponResult.discount_value}% ${isHindi ? 'छूट' : 'off'}`
                          : `${formatPrice(couponResult.discount_value)} ${isHindi ? 'छूट' : 'off'}`}
                      </span>
                    </div>
                    <span className="text-green-700 font-bold">
                      {isHindi ? 'बचत' : 'You save'}: {formatPrice(plan.price - couponResult.final_amount)}
                    </span>
                  </div>
                </motion.div>
              )}

              {couponResult?.valid && couponResult.final_amount === 0 && (
                <Button
                  onClick={handleApplyCoupon}
                  disabled={isProcessing}
                  className="w-full mt-3 md:mt-4 h-12 md:h-14 bg-[#D2E823] hover:bg-[#D2E823]/90 text-[#1A4D2E] font-bold text-base md:text-lg rounded-lg md:rounded-xl touch-manipulation active:scale-[0.98]"
                  data-testid="proceed-free-btn"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  )}
                  {isHindi ? 'निःशुल्क आगे बढ़ें' : 'Proceed for Free'}
                </Button>
              )}
            </motion.div>
          )}
        </div>

        {/* Pay Now Button */}
        <button
          onClick={handlePayNow}
          className="w-full bg-white rounded-xl md:rounded-2xl border-2 border-[#E5E0D1] p-3 md:p-4 flex items-center justify-between active:bg-[#F5F1E8]/50 transition-colors group min-h-[60px] touch-manipulation"
          data-testid="pay-now-btn"
        >
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-9 h-9 md:w-10 md:h-10 bg-[#1A4D2E]/10 rounded-lg md:rounded-xl flex items-center justify-center">
              <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-[#1A4D2E]" />
            </div>
            <div className="text-left">
              <span className="font-medium text-[#1A4D2E] block text-sm md:text-base">
                {isHindi ? 'भुगतान करें' : 'Pay Now'}
              </span>
              <span className="text-xs md:text-sm text-gray-500">
                {formatPrice(couponResult?.valid ? couponResult.final_amount : plan.price)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <span className="px-2 py-0.5 md:py-1 bg-amber-100 text-amber-700 text-[10px] md:text-xs font-medium rounded-full">
              {isHindi ? 'जल्द आ रहा है' : 'Coming Soon'}
            </span>
            <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
          </div>
        </button>

        {/* Security Note */}
        <div className="flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm text-gray-500">
          <Shield className="w-3.5 h-3.5 md:w-4 md:h-4" />
          <span>{isHindi ? 'सुरक्षित भुगतान' : 'Secure payment'}</span>
        </div>
      </div>

      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={onBack}
        className="w-full text-gray-500 hover:text-[#1A4D2E] min-h-[44px] touch-manipulation"
        data-testid="back-btn"
      >
        {isHindi ? '← वापस जाएं' : '← Go Back'}
      </Button>
    </motion.div>
  );
};

export default PaymentGate;
